import type { Item, Addon, Modifier } from "../../../../../api/menu";
import type { ParseVoiceOrderOut } from "../../../../../api/voiceOrders";
import type { BillOut, ProductElementIn } from "../../../../../api/bills";

// Чиста логика на гласовата поръчка: превръща парснатия отговор на модела +
// изборите на потребителя в готови редове (конфигурация, висящи избори,
// проверка на задължителните добавки/модификатори, суми и payload елементи).

export interface VoiceRowConfig {
  quantity: number;
  modifiers: number[];
  // addonId -> { itemId -> количество } — същата форма като кошницата
  addons: Record<number, Record<number, number>>;
  comment: string;
}

export interface PendingModifierChoice {
  choiceKey: string;
  spokenText: string;
  candidateIds: number[];
}

export interface PendingAddonChoice {
  choiceKey: string;
  spokenText: string;
  quantity: number;
  candidates: { addonId: number; itemId: number }[];
}

export interface ResolvedVoiceRow {
  index: number;
  spokenName: string;
  menuItem: Item | null;
  config: VoiceRowConfig;
  pendingModifierChoices: PendingModifierChoice[];
  pendingAddonChoices: PendingAddonChoice[];
  /** Задължителна добавка (тип 2) непокрита или required модификатор без избор
      → редът иска ModifyItemModal преди поръчване. */
  needsConfig: boolean;
  lineTotal: number;
}

export const modifierChoiceKey = (rowIndex: number, modifierIndex: number) =>
  `${rowIndex}:m${modifierIndex}`;

export const addonChoiceKey = (rowIndex: number, addonIndex: number) =>
  `${rowIndex}:a${addonIndex}`;

export const addonOptionValue = (addonId: number, itemId: number) =>
  `${addonId}:${itemId}`;

/** Цена на опция в добавка — променената за групата, иначе основната
    (същото правило като в ModifyItemModal/buildBillElements). */
export const addonOptionPrice = (addon: Addon, itemId: number): number => {
  const addonItem = addon.items.find(
    (candidate) => candidate.itemId === itemId,
  );
  return addonItem?.addonItemPrice ?? addonItem?.price ?? 0;
};

/** Сума на реда: тип 1 добавките са с абсолютно количество, тип 2/3 — по 1 на
    продукт (× количеството) — огледално на сметката в ModifyItemModal. */
export const voiceRowTotal = (
  menuItem: Item,
  config: VoiceRowConfig,
  addonById: Map<number, Addon>,
): number => {
  let absoluteAddonsPrice = 0;
  let perProductAddonsPrice = 0;
  for (const [addonIdText, qtyMap] of Object.entries(config.addons)) {
    const addon = addonById.get(Number(addonIdText));
    if (!addon) continue;
    const groupSum = Object.entries(qtyMap).reduce(
      (sum, [itemIdText, qty]) =>
        sum + addonOptionPrice(addon, Number(itemIdText)) * qty,
      0,
    );
    if (addon.type === 1) absoluteAddonsPrice += groupSum;
    else perProductAddonsPrice += groupSum;
  }
  return (
    (menuItem.price + perProductAddonsPrice) * config.quantity +
    absoluteAddonsPrice
  );
};

interface ResolveParams {
  parsed: ParseVoiceOrderOut;
  chosenProductIds: Record<number, number>;
  chosenModifierIds: Record<string, number>;
  // choiceKey -> "addonId:itemId"
  chosenAddonOptions: Record<string, string>;
  rowOverrides: Record<number, VoiceRowConfig>;
  itemById: Map<number, Item>;
  addonById: Map<number, Addon>;
  modifierById: Map<number, Modifier>;
}

export const resolveVoiceRows = ({
  parsed,
  chosenProductIds,
  chosenModifierIds,
  chosenAddonOptions,
  rowOverrides,
  itemById,
  addonById,
  modifierById,
}: ResolveParams): ResolvedVoiceRow[] =>
  parsed.items.map((item, index) => {
    const productId =
      item.productIds.length === 1
        ? item.productIds[0]
        : (chosenProductIds[index] ?? null);
    const menuItem =
      productId != null && item.productIds.includes(productId)
        ? (itemById.get(productId) ?? null)
        : null;

    const emptyConfig: VoiceRowConfig = {
      quantity: item.quantity,
      modifiers: [],
      addons: {},
      comment: item.comment ?? "",
    };

    if (!menuItem) {
      return {
        index,
        spokenName: item.spokenName,
        menuItem: null,
        config: emptyConfig,
        pendingModifierChoices: [],
        pendingAddonChoices: [],
        needsConfig: false,
        lineTotal: 0,
      };
    }

    const override = rowOverrides[index] ?? null;
    const pendingModifierChoices: PendingModifierChoice[] = [];
    const pendingAddonChoices: PendingAddonChoice[] = [];
    const derivedModifiers: number[] = [];
    const derivedAddons: Record<number, Record<number, number>> = {};

    // Модификатори: кандидатите се стесняват до валидните за избрания продукт
    item.modifiers.forEach((modifierChoice, modifierIndex) => {
      const candidateIds = modifierChoice.modifierIds.filter(
        (id) => menuItem.modifierIds.includes(id) && modifierById.has(id),
      );
      if (candidateIds.length === 0) return;
      const key = modifierChoiceKey(index, modifierIndex);
      const chosen = chosenModifierIds[key];
      const resolved =
        candidateIds.length === 1
          ? candidateIds[0]
          : candidateIds.includes(chosen)
            ? chosen
            : null;
      if (resolved != null) {
        if (!derivedModifiers.includes(resolved)) derivedModifiers.push(resolved);
      } else if (!override) {
        pendingModifierChoices.push({
          choiceKey: key,
          spokenText: modifierChoice.spokenText,
          candidateIds,
        });
      }
    });

    // Добавки: кандидат = валидна двойка (група на продукта, опция в групата)
    item.addons.forEach((addonChoice, addonIndex) => {
      const candidates = addonChoice.options.filter(
        (option) =>
          menuItem.addonIds.includes(option.addonId) &&
          addonById
            .get(option.addonId)
            ?.items.some((ai) => ai.itemId === option.itemId) &&
          itemById.has(option.itemId),
      );
      if (candidates.length === 0) return;
      const key = addonChoiceKey(index, addonIndex);
      const chosenValue = chosenAddonOptions[key];
      const resolved =
        candidates.length === 1
          ? candidates[0]
          : (candidates.find(
              (option) =>
                addonOptionValue(option.addonId, option.itemId) === chosenValue,
            ) ?? null);
      if (resolved) {
        const group = (derivedAddons[resolved.addonId] ??= {});
        group[resolved.itemId] =
          (group[resolved.itemId] ?? 0) + addonChoice.quantity;
      } else if (!override) {
        pendingAddonChoices.push({
          choiceKey: key,
          spokenText: addonChoice.spokenText,
          quantity: addonChoice.quantity,
          candidates,
        });
      }
    });

    // Коментарът от гласа влиза в конфигурацията (?? "" пази срещу стар BE)
    const config: VoiceRowConfig = override ?? {
      quantity: item.quantity,
      modifiers: derivedModifiers,
      addons: derivedAddons,
      comment: item.comment ?? "",
    };

    // Задължителните: тип 2 групи с избрани < min(maxQty, налични опции) — същото
    // правило като гейта „Добави" на ModifyItemModal (група без налична опция не
    // се изисква) — или required модификатор без нито един избран.
    const requiredGroupUnfulfilled = menuItem.addonIds.some((addonId) => {
      const addon = addonById.get(addonId);
      if (!addon || addon.type !== 2) return false;
      const availableOptionsCount = addon.items.filter((option) =>
        itemById.has(option.itemId),
      ).length;
      if (availableOptionsCount === 0) return false;
      const selectedCount = Object.keys(config.addons[addonId] ?? {}).length;
      return selectedCount < Math.min(addon.maxQty || 1, availableOptionsCount);
    });
    const hasRequiredModifier = menuItem.modifierIds.some(
      (id) => modifierById.get(id)?.isRequired,
    );
    const needsConfig =
      requiredGroupUnfulfilled ||
      (hasRequiredModifier && config.modifiers.length === 0);

    return {
      index,
      spokenName: item.spokenName,
      menuItem,
      config,
      pendingModifierChoices,
      pendingAddonChoices,
      needsConfig,
      lineTotal: voiceRowTotal(menuItem, config, addonById),
    };
  });

/** Строи payload елементите за POST /bills — огледално на buildBillElements
    в Ordering.tsx (финални количества на добавките, коментар като модификатор). */
export const buildVoiceElements = (
  rows: ResolvedVoiceRow[],
  addonById: Map<number, Addon>,
  itemById: Map<number, Item>,
): ProductElementIn[] =>
  rows
    .filter((row): row is ResolvedVoiceRow & { menuItem: Item } =>
      Boolean(row.menuItem),
    )
    .map((row) => {
      const { menuItem, config } = row;
      // stationIds е гарантирано непразен (виж Item) — ?? 1 е типова застраховка
      const stationId = menuItem.stationIds?.[0] ?? 1;
      return {
        productId: menuItem.id,
        stationId,
        quantity: config.quantity.toString(),
        price: menuItem.price.toString(),
        lockedPrice: menuItem.lockedPrice,
        percent: "0",
        // БАЗАТА на реда, БЕЗ добавките — точно като buildBillElements в
        // Ordering.tsx. BE сумира реда И всяка добавка отделно, затова
        // row.lineTotal (сумата С добавките, за прегледа) би ги начислил два пъти.
        lineTotal: (menuItem.price * config.quantity).toString(),
        status: "0",
        modifiers: [
          ...config.modifiers.map((modifierId) => ({
            modifier_id: modifierId.toString(),
          })),
          ...(config.comment ? [{ modifier_id: config.comment }] : []),
        ],
        addons: Object.entries(config.addons).flatMap(([addonIdText, qtyMap]) =>
          Object.entries(qtyMap).map(([itemIdText, qty]) => {
            const addonId = Number(addonIdText);
            const optionItemId = Number(itemIdText);
            const addon = addonById.get(addonId);
            const price = addon ? addonOptionPrice(addon, optionItemId) : 0;
            const optionMenuItem = itemById.get(optionItemId);
            const addonStationId = optionMenuItem?.stationIds?.[0] ?? 1;
            // Финално количество (BE не умножава): тип 1 е абсолютно,
            // тип 2/3 — по 1 на продукт × бройката (легаси).
            const absolute = String(addon?.type) === "1";
            const effectiveQty = absolute ? qty : qty * config.quantity;
            return {
              addition_item_id: optionItemId,
              station_id: addonStationId,
              quantity: effectiveQty.toString(),
              price: price.toString(),
              percent: "0",
              total_sum: (price * effectiveQty).toString(),
            };
          }),
        ),
      };
    });

/**
 * Отворената сметка, в която гласовото „отвори маса N" допоръчва (иначе —
 * нова маса). Името съвпада без значение на интервалите в краищата и на
 * главните/малките. При ЛИЧНА номерация всеки сервитьор има своя „1" — само
 * МОЯТА (огледало на `useOpenNextTable`); няма ли моя, заявката е нова маса и
 * BE сам влиза в празната сметка от „Настани" или отваря нова моя. Чуждата
 * видима маса (с `view_all_users_bills`) не бива да поема гласа ми.
 */
export const voiceTargetBill = <TBill extends Pick<BillOut, "status" | "name" | "waiter_id">>(
  bills: TBill[],
  tableName: string,
  personalNumberingEnabled: boolean,
  currentUserId: number | null,
): TBill | null => {
  const wanted = tableName.trim().toLowerCase();
  return (
    bills.find(
      (bill) =>
        bill.status !== "closed" &&
        (!personalNumberingEnabled || bill.waiter_id === currentUserId) &&
        bill.name.trim().toLowerCase() === wanted,
    ) ?? null
  );
};
